import { fetchWithRetry } from '@glow/common'
import replace from 'lodash-es/replace'
import {
  ActionType,
  GET_DELIVERY_SERVICES_OPTIMUS,
  GET_DEPARTMENT_PICKUP_SERVICES_FROM_MATRIX,
  GET_DEPARTMENT_SERVICES_OPTIMUS,
  GET_HD_SERVICES_CHECKOUT,
  GET_PICKUP_SERVICES_OPTIMUS,
  GET_SERVICES_OPTIMUS,
  SAVED_SELECTED_ORDER_IDS,
  VALIDATE_POSTAL_CODE
} from '../actions/actionTypes'
import { AnyData, ThunkDispatch } from '../actions/creators/baseHelpers'
import { isReadOnlyUser } from '../actions/creators/helpers'
import { addNoAccessNotification } from '../actions/creators/helpersNotifications'
import { IErrorProps } from '../selectors/optimizeJobResultsSelector'
import { OrderIdType } from '../types/coreEntitiesTypes'
import { stripBase, withBase } from '../utils/appBase'
import { AppStateType } from '../utils/appStateReduxStore'
import { isNotBlank } from '../utils/inputValidation'
import { isAuthPath, isOpenPath } from '../utils/loginUtils'

export interface ConsumedResponse extends Response {
  readBody?: any
}

export function Http(baseUrl: string) {
  const fullUrl = (path: string) => {
    return baseUrl + path
  }

  const defaultHeaders = () => ({ 'X-Location': window.location.pathname })

  const fetchGet = (path: string): Promise<Response> =>
    fetchWithRetry(fullUrl(path), {
      method: 'GET',
      credentials: 'include',
      headers: defaultHeaders()
    })

  const fetchPost = (uriPath: string, body: AnyData) =>
    fetch(fullUrl(uriPath), {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(body),
      headers: { ...defaultHeaders(), 'Content-Type': 'application/json' }
    })

  /** A backend query as a plain promise, for react-query. The backend sends no body for no result. */
  const query = async <T>(name: string, params: AnyData, empty: T): Promise<T> => {
    const response = await fetchPost(`/query/${name}`, { query: name, params })
    if (!response.ok) {
      return Promise.reject(response)
    }
    const text = await response.text()
    return text ? (JSON.parse(text) as T) : empty
  }

  const asyncGet = (uriPath: string, actionType: ActionType) => asyncRequest(fetchGet(uriPath), actionType)

  const asyncPostRaw = (actionType: ActionType, uri: string, data: any, headers?: HeadersInit) =>
    asyncRequest(
      fetch(fullUrl(uri), {
        credentials: 'include',
        method: 'POST',
        body: data,
        headers
      }),
      actionType
    )

  const asyncRaw = (
    actionType: ActionType,
    method: string,
    path: string,
    body?: BodyInit | null,
    headers?: HeadersInit
  ) =>
    asyncRequest(
      fetch(fullUrl(path), {
        credentials: 'include',
        method,
        body,
        headers
      }),
      actionType
    )

  const asyncPostFormData = (actionType: ActionType, uri: string, data: FormData) =>
    asyncPostRaw(actionType, uri, data, defaultHeaders())

  const asyncPostJson = (actionType: ActionType, uri: string, data: AnyData) =>
    asyncPostRaw(actionType, uri, JSON.stringify(data), { ...defaultHeaders(), 'Content-Type': 'application/json' })

  const asyncQuery = (actionType: ActionType, data: QueryData) =>
    asyncRequest(
      fetchWithRetry(fullUrl(`/query/${actionType.name}`), {
        credentials: 'include',
        method: 'POST',
        headers: {
          ...defaultHeaders(),
          'content-type': 'application/json'
        },
        body: JSON.stringify(data)
      }),
      actionType,
      data
    )

  const asyncCommand =
    (actionType: ActionType, data: AnyData) => (dispatch: ThunkDispatch, getState: () => AppStateType) => {
      if (isReadOnlyUser(getState().get('user'))) {
        const msg = 'no access for user with role ' + getState().getIn(['user', 'role'])
        console.warn(msg)
        dispatch(addNoAccessNotification())
        throw msg
      } else return dispatch(asyncPostJson(actionType, `/command/${actionType.name}`, data))
    }

  const asyncDeleteCommand =
    (actionType: ActionType, data: AnyData, onSuccessRedirectUrl?: string, push?: (url: string) => void) =>
    async (dispatch: ThunkDispatch, getState: () => AppStateType) => {
      if (isReadOnlyUser(getState().get('user'))) {
        console.warn('no access for user with role', getState().getIn(['user', 'role']))
        return dispatch(addNoAccessNotification())
      }

      return dispatch(asyncCommand(actionType, data)).then(() => onSuccessRedirectUrl && push?.(onSuccessRedirectUrl))
    }

  return {
    fullUrl,
    fetchGet,
    fetchPost,
    query,
    asyncGet,
    asyncPostRaw,
    asyncRaw,
    asyncPostFormData,
    asyncPostJson,
    asyncQuery,
    asyncCommand,
    asyncDeleteCommand
  }
}

export type QueryData = {
  query: string
  params: AnyData
}

export const api = Http('/api')
export const openApi = Http('/openapi')
export const orderApi = Http('/glow-orders/api/external')
export const checkoutApi = Http('/checkout/api')
export const parcelHubApi = Http('/parcelhub-consumer/api')

export interface ErrorType {
  body: any
  response: ConsumedResponse
  path: string
  url: string
  status: number
}

const paramsOrNull = (data?: AnyData) => (data ? data.params : null)

const asyncRequest = (requestFn: Promise<ConsumedResponse>, actionType: ActionType, data?: AnyData) => {
  return (dispatch: ThunkDispatch, getState: () => AppStateType) => {
    dispatch({
      type: actionType.REQUEST,
      params: paramsOrNull(data)
    })

    return requestFn
      .then((response) => {
        const contentType = response.headers.get('content-type')
        const isJson = !!contentType && contentType.indexOf('application/json') !== -1
        // Some gateways (checkout) answer an ended session by redirecting to the login page, which fetch follows
        const sessionEnded = response.redirected && !isJson
        const r = isJson ? response.json() : response.text()
        return r.then((jsonBody) => {
          if (response.ok && !sessionEnded) {
            dispatch({
              params: paramsOrNull(data),
              isEntity: actionType.isEntity,
              type: actionType.SUCCESS,
              status: response.status,
              body: jsonBody
            })

            response.readBody = jsonBody
            return response
          } else {
            const pathToCheck = stripBase(window.location.pathname)
            if (
              (sessionEnded || (response.status === 401 && !getState()?.get('user')?.has('name'))) &&
              pathToCheck !== '/' &&
              pathToCheck !== '/login' &&
              !isAuthPath() &&
              !isOpenPath()
            ) {
              window.location.href = withBase(`/?redirect-to=${stripBase(window.location.pathname)}`)
            } else {
              dispatch({
                type: actionType.FAILURE,
                status: response.status,
                body: jsonBody,
                url: response.url
              })
            }
            return Promise.reject({
              body: jsonBody,
              response,
              path: window.location.pathname,
              url: response.url,
              status: response.status
            })
          }
        })
      })
      .catch((err) => {
        console.error({
          type: 'HTTP-Failure',
          action: actionType.REQUEST,
          err: JSON.stringify(err),
          name: err.name,
          message: err.message,
          stack: err.stack
        })
        return Promise.reject(err)
      })
  }
}

export const getFirstErrorMessage = (err: any): IErrorProps | undefined =>
  Array.isArray(err.body) ? (err.body.find((x: IErrorProps) => x.description !== '') as IErrorProps) : undefined

export const getStandardServicesForDepartment = (departmentAlystraId: string) => (dispatch: ThunkDispatch) => {
  return dispatch(
    orderApi.asyncGet(`/optimus/services?departmentAlystraId=${departmentAlystraId}`, GET_DEPARTMENT_SERVICES_OPTIMUS)
  )
}

export const getPickupServicesFromOptimus =
  (customerNumber: string, subcustomer: string | null, pickupCountryCode: string, pickupPostalCode: string) =>
  (dispatch: ThunkDispatch) => {
    const pickupPostalCodeTrimmed = isNotBlank(pickupPostalCode) ? replace(pickupPostalCode, ' ', '') : null

    const servicesUrl = `/optimus/services?alystraCustomerNo=${customerNumber || ''}&subcustomer=${
      subcustomer || ''
    }&pickupCountryCode=${pickupCountryCode || ''}&pickupPostalCode=${pickupPostalCodeTrimmed || ''}`

    return dispatch(orderApi.asyncGet(servicesUrl, GET_PICKUP_SERVICES_OPTIMUS))
  }

export const getDeliveryServicesFromOptimus =
  (customerNumber: string, subcustomer: string | null, deliveryCountryCode?: string, deliveryPostalCode?: string) =>
  (dispatch: ThunkDispatch) => {
    const deliveryPostalCodeTrimmed =
      deliveryPostalCode && isNotBlank(deliveryPostalCode) ? replace(deliveryPostalCode, ' ', '') : null

    const servicesUrl = `/optimus/services?alystraCustomerNo=${customerNumber || ''}&subcustomer=${
      subcustomer || ''
    }&deliveryCountryCode=${deliveryCountryCode || ''}&deliveryPostalCode=${deliveryPostalCodeTrimmed || ''}`
    return dispatch(orderApi.asyncGet(servicesUrl, GET_DELIVERY_SERVICES_OPTIMUS))
  }

export const getServicesFromOptimus =
  (
    customerNumber: string,
    subcustomer: string | null,
    pickupCountryCode: string,
    pickupPostalCode: string,
    deliveryCountryCode?: string,
    deliveryPostalCode?: string
  ) =>
  (dispatch: ThunkDispatch) => {
    const pickupPostalCodeTrimmed = isNotBlank(pickupPostalCode) ? replace(pickupPostalCode, ' ', '') : null
    const deliveryPostalCodeTrimmed =
      deliveryPostalCode && isNotBlank(deliveryPostalCode) ? replace(deliveryPostalCode, ' ', '') : null

    const servicesUrl = `/optimus/services?alystraCustomerNo=${customerNumber || ''}&subcustomer=${
      subcustomer || ''
    }&pickupCountryCode=${pickupCountryCode || ''}&pickupPostalCode=${
      pickupPostalCodeTrimmed || ''
    }&deliveryCountryCode=${deliveryCountryCode || ''}&deliveryPostalCode=${deliveryPostalCodeTrimmed || ''}`
    return dispatch(orderApi.asyncGet(servicesUrl, GET_SERVICES_OPTIMUS))
  }

export const getHdServices =
  (customerNumber: string, subcustomer: string | null, deliveryCountryCode: string, deliveryPostalCode: string) =>
  (dispatch: ThunkDispatch) => {
    const deliveryPostalCodeTrimmed =
      deliveryPostalCode && isNotBlank(deliveryPostalCode) ? replace(deliveryPostalCode, ' ', '') : null

    const servicesUrl = `/hd-services?customerNumber=${customerNumber}&subcustomer=${
      subcustomer || ''
    }&deliveryCountryCode=${deliveryCountryCode}&deliveryPostalCode=${deliveryPostalCodeTrimmed}`
    return dispatch(checkoutApi.asyncGet(servicesUrl, GET_HD_SERVICES_CHECKOUT))
  }

export const buildQuery = (basePath: string, params: Record<string, any>): string => {
  const nonNull = Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '') // keep 0 / false if needed later adjust spec
  const query = new URLSearchParams(nonNull)
  return query ? `${basePath}?${query}` : basePath
}

export const getDepartmentPickupServicesFromMatrix = (
  departmentExternalId: string | null,
  countryCode: string,
  postalCode: string
) =>
  getDepartmentServicesFromMatrix(
    GET_DEPARTMENT_PICKUP_SERVICES_FROM_MATRIX,
    departmentExternalId,
    countryCode,
    postalCode
  )

const getDepartmentServicesFromMatrix =
  (actionType: ActionType, departmentExternalId: string | null, countryCode: string, postalCode: string) =>
  (dispatch: ThunkDispatch) => {
    const params: Record<string, string> = {}
    if (departmentExternalId) params.departmentExternalId = departmentExternalId
    if (countryCode) params.countryCode = countryCode
    const postalCodeTrimmed = postalCode && isNotBlank(postalCode) ? replace(postalCode, ' ', '') : null
    if (postalCodeTrimmed) params.postalCode = postalCodeTrimmed

    const servicesUrl = buildQuery('/department-matrix/services', params)
    return dispatch(checkoutApi.asyncGet(servicesUrl, actionType))
  }

export const validatePostalCode = (postalCode: string, countryCode: string) => (dispatch: ThunkDispatch) =>
  dispatch(
    asyncRequest(
      fetch(`/openapi/validate/postalCode?countryCode=${countryCode}&postalCode=${postalCode}`, {
        method: 'GET',
        credentials: 'include'
      }),
      VALIDATE_POSTAL_CODE
    )
  ).then(
    (res) => res.readBody.isValid,
    (err) => {
      console.error('validatePostalCode', err)
      return false
    }
  )

export const asyncOptimusCreateOrders =
  (actionType: ActionType, data: AnyData) => (dispatch: ThunkDispatch, getState: () => AppStateType) => {
    if (isReadOnlyUser(getState().get('user'))) {
      const msg = 'no access for user with role ' + getState().getIn(['user', 'role'])
      console.warn(msg)
      dispatch(addNoAccessNotification())
      throw msg
    } else return dispatch(orderApi.asyncPostJson(actionType, '/create-order', data))
  }

export const saveSelectedOrderIds = (selectedOrderIds: OrderIdType[]) => (dispatch: ThunkDispatch) => {
  return dispatch({
    type: SAVED_SELECTED_ORDER_IDS,
    selectedOrderIds,
    url: document.location.href
  })
}
